import { z } from "zod";
import {
  CONTRACT_PERIODICITIES,
  CONTRACT_STATUSES,
  MAX_TITLE_CENTS,
  PERIOD_PRINTS,
  contractProblem,
  isCompetence,
  type ContractPeriodicity,
  type ContractStatus,
  type PeriodPrint,
  type TitleStatus,
} from "@cx/domain";
import { listQuery, type ListResult } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import { nfseServiceInput, type ServiceInvoiceStatus } from "./service-invoices";

// ===== Contratos e cobranças recorrentes (T2.8, RF-SER-01/02) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(1, "Valor deve ser maior que zero")
    .max(MAX_TITLE_CENTS, "Valor acima do limite");

export const CONTRACT_INVOICE_MODES = ["none", "on_charge"] as const;
export type ContractInvoiceMode = (typeof CONTRACT_INVOICE_MODES)[number];
/** Cobrança automática junto com a conta: nenhuma ou Pix pelo provedor configurado (boleto: T2.10). */
export const CONTRACT_CHARGE_KINDS = ["none", "pix"] as const;
export type ContractChargeKind = (typeof CONTRACT_CHARGE_KINDS)[number];

export const competence = z
  .string("Informe a competência")
  .refine(isCompetence, "Competência inválida (use mês/ano)");

/**
 * Cadastro do contrato (RF-SER-01). Número em branco = o próximo da sequência. `service` nulo = o serviço padrão da
 * configuração da NFS-e.
 */
export const contractInput = z
  .object({
    contactId: ulidParam,
    billToContactId: nullish(ulidParam),
    number: z
      .number()
      .int()
      .min(1, "Número inválido")
      .max(999_999_999)
      .nullish()
      .transform((v) => v ?? null),
    description: text(255),
    status: z.enum(CONTRACT_STATUSES, "Situação inválida").default("active"),
    contractDate: businessDate,
    amountCents: cents("o valor"),
    baseDate: nullish(businessDate),
    dueMonthOffset: z.union([z.literal(0), z.literal(1), z.literal(2)]).default(0),
    dueDay: z.number("Informe o dia do vencimento").int().min(1, "Dia de 1 a 31").max(31, "Dia de 1 a 31"),
    periodicity: z.enum(CONTRACT_PERIODICITIES, "Periodicidade inválida").default("monthly"),
    expectedTotalCents: nullish(cents("o valor total previsto")),
    periodPrint: z.enum(PERIOD_PRINTS).default("none"),
    invoiceMode: z.enum(CONTRACT_INVOICE_MODES).default("none"),
    chargeKind: z.enum(CONTRACT_CHARGE_KINDS).default("none"),
    service: nullish(nfseServiceInput),
    paymentMethodId: nullish(ulidParam),
    financialAccountId: nullish(ulidParam),
    financialCategoryId: nullish(ulidParam),
    sellerId: nullish(ulidParam),
    notes: text(2000),
  })
  .superRefine((v, c) => {
    const p = contractProblem({ ...v, baseDate: v.baseDate });
    if (p) c.addIssue({ code: "custom", message: p, path: ["amountCents"] });
  });
export type ContractInput = z.output<typeof contractInput>;
export type ContractInputBody = z.input<typeof contractInput>;

export const contractListQuery = listQuery.extend({
  status: z.enum(CONTRACT_STATUSES).optional(),
  contactId: ulidParam.optional(),
});
export type ContractListQuery = z.output<typeof contractListQuery>;

type Ref = { id: string; name: string } | null;

export type ContractListItem = {
  id: string;
  number: number;
  contact: { id: string; name: string };
  billTo: Ref;
  description: string | null;
  status: ContractStatus;
  amountCents: number;
  periodicity: ContractPeriodicity;
  dueDay: number;
  dueMonthOffset: number;
  invoiceMode: ContractInvoiceMode;
  chargeKind: ContractChargeKind;
  /** Última competência com cobrança ativa (null = nunca cobrado). */
  lastCompetence: string | null;
};
export type ContractList = ListResult<ContractListItem>;

export type ContractBillingRow = {
  id: string;
  competence: string;
  amountCents: number;
  dueDate: string;
  status: "active" | "canceled";
  title: { id: string; status: TitleStatus; paidCents: number } | null;
  serviceInvoice: { id: string; status: ServiceInvoiceStatus; nfseNumber: string | null } | null;
  createdAt: string;
};

export type ContractView = Omit<ContractInput, "number"> & {
  id: string;
  number: number;
  contact: { id: string; name: string };
  billTo: Ref;
  paymentMethod: Ref;
  financialAccount: Ref;
  financialCategory: Ref;
  seller: Ref;
  billedCents: number;
  billings: ContractBillingRow[];
  createdAt: string;
  updatedAt: string;
};

export type ContractFormOptions = {
  paymentMethods: { id: string; name: string }[];
  financialAccounts: { id: string; name: string }[];
  financialCategories: { id: string; name: string }[];
  sellers: { id: string; name: string }[];
};

// ---------- cobranças ----------

/** "Gerar cobranças" (RF-SER-02): todos os contratos ativos devidos na competência, ou só os escolhidos. */
export const billingRunInput = z.object({
  competence,
  contractIds: z.array(ulidParam).min(1).max(300).optional(),
});
export type BillingRunInput = z.output<typeof billingRunInput>;

export type BillingRunResult = {
  competence: string;
  created: {
    billingId: string;
    contractId: string;
    contractNumber: number;
    titleId: string;
    amountCents: number;
  }[];
  /** Contratos que não cobraram e o motivo (previsto atingido, conta em limite…). */
  skipped: { contractId: string; contractNumber: number; reason: string }[];
  /** NFS-e enviadas, em rascunho (sem permissão/provedor) e com problema de cadastro. */
  nfse: { queued: number; drafts: number; errors: { contractNumber: number; message: string }[] };
  /** Ainda há contratos devidos (teto por rodada): rode de novo. */
  hasMore: boolean;
};

export const contractBillingListQuery = listQuery.extend({
  competence: competence.optional(),
  contractId: ulidParam.optional(),
  status: z.enum(["active", "canceled"]).optional(),
});
export type ContractBillingListQuery = z.output<typeof contractBillingListQuery>;

export type ContractBillingListItem = ContractBillingRow & {
  contract: { id: string; number: number; description: string | null };
  contact: { id: string; name: string };
  cancelReason: string | null;
};
export type ContractBillingList = ListResult<ContractBillingListItem> & {
  totals: { activeCount: number; activeCents: number };
};

const reason = z.string("Informe o motivo").trim().min(3, "Informe o motivo").max(500, "Até 500 caracteres");
export const billingCancelInput = z.object({ reason });
export type BillingCancelInput = z.output<typeof billingCancelInput>;

/** "Excluir cobranças do mês": estorna as cobranças ativas da competência que podem ser estornadas. */
export const billingBulkCancelInput = z.object({ competence, reason });
export type BillingBulkCancelInput = z.output<typeof billingBulkCancelInput>;
export type BillingCancelResult = {
  canceled: string[];
  skipped: { billingId: string; contractNumber: number; reason: string }[];
};

/** Geração automática: dia do mês (null = desligada). */
export const contractSettingsInput = z.object({
  autoDay: z.number().int().min(1, "Dia de 1 a 28").max(28, "Dia de 1 a 28").nullable(),
});
export type ContractSettingsInput = z.output<typeof contractSettingsInput>;
export type ContractSettings = ContractSettingsInput & {
  /** Próxima geração automática (AAAA-MM-DD) ou null. */
  nextRunOn: string | null;
  lastAutoCompetence: string | null;
};

export type { ContractPeriodicity, ContractStatus, PeriodPrint };
